import React from "react";
import { Checkbox } from "@/components/Calendar/ui/checkbox";

interface PharmacistFilterProps {
  selectedPharmacists: string[];
  onPharmacistChange: (pharmacist: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  pharmacists: string[]; // Accept dynamic pharmacist names as prop
  isDarkMode?: boolean;
}

export function PharmacistFilter({
  selectedPharmacists,
  onPharmacistChange,
  onSelectAll,
  pharmacists = [],
  isDarkMode = false,
}: PharmacistFilterProps) {
  return (
    <div
      className={`rounded-lg border p-4 transition-colors duration-300 ${
        isDarkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-200"
      }`}
    >
      <h4
        className={`text-sm font-semibold mb-3 transition-colors duration-300 ${
          isDarkMode ? "text-white" : "text-gray-900"
        }`}
      >
        Pharmacist
      </h4>
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="select-all-pharmacists"
            checked={selectedPharmacists.length === pharmacists.length && pharmacists.length > 0}
            onCheckedChange={onSelectAll}
          />
          <label
            htmlFor="select-all-pharmacists"
            className={`text-sm cursor-pointer transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            Select All
          </label>
        </div>
        <hr
          className={`transition-colors duration-300 ${
            isDarkMode ? "border-gray-600" : "border-gray-200"
          }`}
        />
        {pharmacists.length === 0 ? (
          <p
            className={`text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            No pharmacists available
          </p>
        ) : (
          pharmacists.map((pharmacist) => {
            // Generate initials from pharmacist name
            const initials = pharmacist
              .split(" ")
              .map((word) => word[0])
              .join("")
              .toUpperCase()
              .slice(0, 2);

            // Generate a consistent color based on pharmacist name
            const colors = [
              "bg-blue-500",
              "bg-green-500",
              "bg-purple-500",
              "bg-pink-500",
              "bg-indigo-500",
              "bg-yellow-500",
              "bg-red-500",
              "bg-teal-500",
            ];
            const colorIndex = pharmacist.charCodeAt(0) % colors.length;
            const bgColor = colors[colorIndex];

            return (
              <div key={pharmacist} className="flex items-center space-x-3">
                <Checkbox
                  id={`pharmacist-${pharmacist}`}
                  checked={selectedPharmacists.includes(pharmacist)}
                  onCheckedChange={(checked) =>
                    onPharmacistChange(pharmacist, !!checked)
                  }
                />
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${bgColor}`}
                  title={pharmacist}
                >
                  {initials}
                </div>
                <label
                  htmlFor={`pharmacist-${pharmacist}`}
                  className={`text-sm font-medium cursor-pointer transition-colors duration-300 ${
                    isDarkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  {pharmacist}
                </label>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
